import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { TrendingUp, DollarSign, CalendarClock, ArrowUpRight, ArrowDownRight, Landmark, RefreshCw, Activity, Repeat, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#0A2B1D', '#3A6E53', '#6BA38A', '#9CC4AF', '#C8DFD2', '#254D3D', '#4D8B6F', '#82B79E', '#B4C9BE', '#E2ECE7'];
const EXPENSE_COLORS = ['#B91C1C', '#DC2626', '#EF4444', '#F87171', '#FCA5A5', '#991B1B', '#7F1D1D'];

const OverviewTab = ({ businessId, token, expenses, income, pnlReport, connectedAccounts }) => {
  const [taxData, setTaxData] = useState(null);
  const [healthScore, setHealthScore] = useState(null);
  const [balances, setBalances] = useState([]);
  const [recurringExpenses, setRecurringExpenses] = useState(null);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [balanceCooldown, setBalanceCooldown] = useState(0);
  const [balanceCachedAt, setBalanceCachedAt] = useState(null);

  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    if (!businessId || !token) return;
    const fetchTax = async () => {
      try {
        const res = await axios.get(`${API}/api/mybookie/tax-estimates/${businessId}?year=${new Date().getFullYear()}`, authHeaders);
        setTaxData(res.data);
      } catch (error) {
        console.error("OverviewTab error:", error);
      }
    };
    fetchTax();
  }, [businessId, token, authHeaders]);

  // Fetch Health Score
  useEffect(() => {
    if (!businessId || !token) return;
    const fetchHealth = async () => {
      try {
        const res = await axios.get(`${API}/api/mybookie/health-score/${businessId}`, authHeaders);
        setHealthScore(res.data);
      } catch (error) {
        console.error("OverviewTab error:", error);
      }
    };
    fetchHealth();
  }, [businessId, token, expenses, income, authHeaders]);

  // Fetch Bank Balances
  useEffect(() => {
    if (!businessId || !token) return;
    const fetchBalances = async () => {
      setBalancesLoading(true);
      try {
        const res = await axios.get(`${API}/api/mybookie/plaid/balances/${businessId}`, authHeaders);
        setBalances(res.data.balances || []);
        setBalanceCooldown(res.data.next_refresh_in || 0);
        if (res.data.cached_at) setBalanceCachedAt(res.data.cached_at);
      } catch (error) {
        console.error("OverviewTab error:", error);
      }
      setBalancesLoading(false);
    };
    fetchBalances();
  }, [businessId, token, authHeaders]);

  // Balance cooldown timer
  useEffect(() => {
    if (balanceCooldown <= 0) return;
    const timer = setInterval(() => {
      setBalanceCooldown(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [balanceCooldown]);

  // Fetch Recurring Expenses
  useEffect(() => {
    if (!businessId || !token) return;
    const fetchRecurring = async () => {
      try {
        const res = await axios.get(`${API}/api/mybookie/recurring-expenses/${businessId}`, authHeaders);
        setRecurringExpenses(res.data);
      } catch (error) {
        console.error("OverviewTab error:", error);
      }
    };
    fetchRecurring();
  }, [businessId, token, expenses, authHeaders]);

  const refreshBalances = useCallback(async () => {
    if (balanceCooldown > 0) return;
    setBalancesLoading(true);
    try {
      const res = await axios.get(`${API}/api/mybookie/plaid/balances/${businessId}`, authHeaders);
      setBalances(res.data.balances || []);
      setBalanceCooldown(res.data.next_refresh_in || 0);
      if (res.data.cached_at) setBalanceCachedAt(res.data.cached_at);
    } catch (error) {
        console.error("OverviewTab error:", error);
      }
    setBalancesLoading(false);
  }, [balanceCooldown, businessId, authHeaders]);

  const fmt = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtShort = (n) => {
    if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}k`;
    return `$${n.toFixed(0)}`;
  };

  // Monthly revenue trend (last 12 months)
  const monthlyData = useMemo(() => {
    const months = {};
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-US', { month: 'short' });
      months[key] = { month: label, income: 0, expenses: 0, net: 0 };
    }
    (income || []).forEach(item => {
      const key = item.date?.substring(0, 7);
      if (months[key]) months[key].income += item.amount;
    });
    (expenses || []).forEach(item => {
      const key = item.date?.substring(0, 7);
      if (months[key]) months[key].expenses += item.amount;
    });
    return Object.values(months).map(m => ({ ...m, net: m.income - m.expenses }));
  }, [income, expenses]);

  // Expense breakdown by category
  const expenseByCategory = useMemo(() => {
    const cats = {};
    (expenses || []).forEach(exp => {
      const cat = exp.category_name || 'Uncategorized';
      cats[cat] = (cats[cat] || 0) + exp.amount;
    });
    return Object.entries(cats)
      .map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 7);
  }, [expenses]);

  // Income by source
  const incomeBySource = useMemo(() => {
    const sources = {};
    (income || []).forEach(inc => {
      const src = inc.source || inc.description || 'Other';
      sources[src] = (sources[src] || 0) + inc.amount;
    });
    return Object.entries(sources)
      .map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 7);
  }, [income]);

  // This month vs last month
  const comparison = useMemo(() => {
    const now = new Date();
    const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const lastDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonth = `${lastDate.getFullYear()}-${String(lastDate.getMonth() + 1).padStart(2, '0')}`;

    let thisIncome = 0, lastIncome = 0, thisExpense = 0, lastExpense = 0;
    (income || []).forEach(i => {
      const m = i.date?.substring(0, 7);
      if (m === thisMonth) thisIncome += i.amount;
      if (m === lastMonth) lastIncome += i.amount;
    });
    (expenses || []).forEach(e => {
      const m = e.date?.substring(0, 7);
      if (m === thisMonth) thisExpense += e.amount;
      if (m === lastMonth) lastExpense += e.amount;
    });

    const incomeChange = lastIncome > 0 ? ((thisIncome - lastIncome) / lastIncome * 100) : 0;
    const expenseChange = lastExpense > 0 ? ((thisExpense - lastExpense) / lastExpense * 100) : 0;

    return {
      thisIncome, lastIncome, thisExpense, lastExpense,
      thisNet: thisIncome - thisExpense,
      incomeChange: Math.round(incomeChange),
      expenseChange: Math.round(expenseChange)
    };
  }, [income, expenses]);

  // Upcoming tax deadlines
  const upcomingDeadlines = useMemo(() => {
    if (!taxData?.quarters) return [];
    const now = new Date().toISOString().split('T')[0];
    return taxData.quarters
      .filter(q => q.due_date >= now)
      .slice(0, 2);
  }, [taxData]);

  const totalIncome = (income || []).reduce((s, i) => s + i.amount, 0);
  const totalExpenses = (expenses || []).reduce((s, e) => s + e.amount, 0);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-[#E6E8E6] rounded-sm p-3 shadow-lg text-xs">
        <p className="font-medium text-[#111614] mb-1">{label}</p>
        {payload.map((p) => (
          <p key={p.dataKey || p.name} style={{ color: p.color }}>{p.name}: {fmt(p.value)}</p>
        ))}
      </div>
    );
  };

  // Health score arc
  const HealthScoreArc = ({ score, grade, color }) => {
    const radius = 54;
    const circumference = Math.PI * radius;
    const progress = (score / 100) * circumference;
    return (
      <div className="relative w-[140px] h-[80px] mx-auto" data-testid="health-score-arc">
        <svg viewBox="0 0 120 70" className="w-full h-full">
          <path d="M 6 64 A 54 54 0 0 1 114 64" fill="none" stroke="#E6E8E6" strokeWidth="8" strokeLinecap="round" />
          <path d="M 6 64 A 54 54 0 0 1 114 64" fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
            strokeDasharray={`${progress} ${circumference}`}
            style={{ transition: 'stroke-dasharray 1s ease-out' }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-0">
          <span className="text-3xl font-bold" style={{ color }}>{score}</span>
          <span className="text-[10px] text-[#5C6661] -mt-1">{grade}</span>
        </div>
      </div>
    );
  };

  const freqLabel = { monthly: '/mo', weekly: '/wk', 'bi-weekly': '/2wk', quarterly: '/qtr' };

  return (
    <div className="space-y-6" data-testid="overview-tab">
      {/* Row 1: Health Score + Bank Balances */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Business Health Score */}
        <Card className="border-[#E6E8E6]" data-testid="health-score-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#0A2B1D]" />
              Business Health Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            {healthScore ? (
              <div>
                <HealthScoreArc score={healthScore.score} grade={healthScore.grade} color={healthScore.color} />
                <div className="grid grid-cols-2 gap-2 mt-4">
                  {Object.values(healthScore.breakdown).map((item) => (
                    <div key={item.label} className="flex items-center justify-between bg-[#F6F5F2] rounded-sm px-3 py-2">
                      <span className="text-xs text-[#5C6661]">{item.label}</span>
                      <span className="text-xs font-semibold text-[#111614]">{item.score}/{item.max}</span>
                    </div>
                  ))}
                </div>
                {healthScore.tips?.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {healthScore.tips.map((tip) => (
                      <div key={tip} className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 rounded-sm px-3 py-1.5">
                        <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                        <span>{tip}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="h-[180px] flex items-center justify-center text-[#8A948F] text-sm">Loading...</div>
            )}
          </CardContent>
        </Card>

        {/* Bank Balances Widget */}
        <Card className="border-[#E6E8E6]" data-testid="bank-balances-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Landmark className="w-4 h-4 text-[#0A2B1D]" />
                Bank Balances
              </CardTitle>
              {balances.length > 0 && (
                <div className="flex items-center gap-2">
                  {balanceCachedAt && (
                    <span className="text-[10px] text-[#8A948F]">
                      Updated {(() => {
                        const mins = Math.floor((Date.now() - new Date(balanceCachedAt).getTime()) / 60000);
                        return mins < 1 ? 'just now' : `${mins}m ago`;
                      })()}
                    </span>
                  )}
                  <Button variant="ghost" size="sm" onClick={refreshBalances} disabled={balancesLoading || balanceCooldown > 0} className="h-7 px-2" data-testid="refresh-balances-btn" title={balanceCooldown > 0 ? `Refresh available in ${Math.ceil(balanceCooldown / 60)}m` : 'Refresh balances'}>
                    <RefreshCw className={`w-3.5 h-3.5 ${balancesLoading ? 'animate-spin' : ''}`} />
                    {balanceCooldown > 0 && <span className="text-[10px] ml-1 text-[#8A948F]">{Math.ceil(balanceCooldown / 60)}m</span>}
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {balancesLoading && balances.length === 0 ? (
              <div className="h-[180px] flex items-center justify-center text-[#8A948F] text-sm">
                <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Fetching balances...
              </div>
            ) : balances.length > 0 ? (
              <div className="space-y-3">
                {balances.map((acc) => (
                  <div key={acc.account_id} className="flex items-center justify-between bg-[#F6F5F2] rounded-sm px-4 py-3 border border-[#E6E8E6]" data-testid={`balance-item-${acc.account_id}`}>
                    <div>
                      <p className="text-sm font-medium text-[#111614]">{acc.name}</p>
                      <p className="text-xs text-[#5C6661]">{acc.institution_name} {acc.mask ? `****${acc.mask}` : ''}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#0A2B1D]">{fmt(acc.current_balance)}</p>
                      {acc.available_balance != null && acc.available_balance !== acc.current_balance && (
                        <p className="text-xs text-[#5C6661]">{fmt(acc.available_balance)} available</p>
                      )}
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between border-t border-[#E6E8E6] pt-3 mt-2">
                  <span className="text-sm font-medium text-[#5C6661]">Total Balance</span>
                  <span className="text-lg font-bold text-[#0A2B1D]">{fmt(balances.reduce((s, a) => s + (a.current_balance || 0), 0))}</span>
                </div>
              </div>
            ) : (
              <div className="h-[180px] flex flex-col items-center justify-center text-center">
                <Landmark className="w-8 h-8 text-[#B4C9BE] mb-2" />
                <p className="text-sm text-[#5C6661]">No bank accounts connected</p>
                <p className="text-xs text-[#8A948F] mt-1">Connect a bank to see live balances here</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Month Comparison Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-[#5C6661]">This Month Income</span>
              <ArrowUpRight className="w-4 h-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-[#111614]" data-testid="this-month-income">{fmt(comparison.thisIncome)}</p>
            {comparison.incomeChange !== 0 && (
              <p className={`text-xs mt-1 ${comparison.incomeChange > 0 ? 'text-green-600' : 'text-red-500'}`}>
                {comparison.incomeChange > 0 ? '+' : ''}{comparison.incomeChange}% vs last month
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-400">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-[#5C6661]">This Month Expenses</span>
              <ArrowDownRight className="w-4 h-4 text-red-400" />
            </div>
            <p className="text-2xl font-bold text-[#111614]" data-testid="this-month-expenses">{fmt(comparison.thisExpense)}</p>
            {comparison.expenseChange !== 0 && (
              <p className={`text-xs mt-1 ${comparison.expenseChange < 0 ? 'text-green-600' : 'text-red-500'}`}>
                {comparison.expenseChange > 0 ? '+' : ''}{comparison.expenseChange}% vs last month
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-[#0A2B1D]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-[#5C6661]">Net Profit (Month)</span>
              <DollarSign className="w-4 h-4 text-[#0A2B1D]" />
            </div>
            <p className={`text-2xl font-bold ${comparison.thisNet >= 0 ? 'text-[#0A2B1D]' : 'text-red-600'}`} data-testid="net-profit-month">
              {fmt(comparison.thisNet)}
            </p>
          </CardContent>
        </Card>

        <Card className={`border-l-4 ${upcomingDeadlines.length > 0 ? 'border-l-amber-400' : 'border-l-[#E6E8E6]'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-[#5C6661]">Next Tax Deadline</span>
              <CalendarClock className="w-4 h-4 text-amber-500" />
            </div>
            {upcomingDeadlines.length > 0 ? (
              <>
                <p className="text-lg font-bold text-[#111614]">{upcomingDeadlines[0].quarter}</p>
                <p className="text-xs text-[#5C6661]">Due: {upcomingDeadlines[0].due_date}</p>
                <p className="text-xs font-medium text-amber-600 mt-0.5">{fmt(upcomingDeadlines[0].total_estimated_tax)} est.</p>
              </>
            ) : (
              <p className="text-sm text-[#5C6661]">No upcoming deadlines</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recurring Expenses Card */}
      {recurringExpenses && (recurringExpenses.recurring?.length > 0 || (expenses || []).length > 0) && (
        <Card className="border-[#E6E8E6]" data-testid="recurring-expenses-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Repeat className="w-4 h-4 text-[#0A2B1D]" />
                Recurring Expenses
              </CardTitle>
              {recurringExpenses.total_monthly > 0 && (
                <span className="text-sm font-semibold text-[#0A2B1D] bg-[#E2ECE7] px-3 py-1 rounded-full" data-testid="recurring-total">
                  {fmt(recurringExpenses.total_monthly)}/mo
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {recurringExpenses.recurring?.length > 0 ? (
              <div className="space-y-2">
                {recurringExpenses.recurring.slice(0, 6).map((item) => (
                  <div key={`${item.vendor}-${item.frequency}`} className="flex items-center justify-between py-2 px-3 bg-[#F6F5F2] rounded-sm" data-testid={`recurring-item-${item.vendor}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#E2ECE7] flex items-center justify-center">
                        <Repeat className="w-3.5 h-3.5 text-[#0A2B1D]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#111614] capitalize">{item.vendor}</p>
                        <p className="text-xs text-[#8A948F]">{item.category} &middot; {item.frequency} &middot; {item.occurrences}x</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-[#111614]">{fmt(item.avg_amount)}{freqLabel[item.frequency] || ''}</p>
                      <p className="text-xs text-[#8A948F]">{fmt(item.monthly_cost)}/mo</p>
                    </div>
                  </div>
                ))}
                {recurringExpenses.recurring.length > 6 && (
                  <p className="text-xs text-center text-[#8A948F] pt-1">
                    +{recurringExpenses.recurring.length - 6} more recurring expenses
                  </p>
                )}
              </div>
            ) : (
              <div className="py-6 text-center text-[#8A948F] text-sm">
                <Repeat className="w-6 h-6 mx-auto mb-2 opacity-40" />
                No recurring patterns detected yet. Add more transactions to enable detection.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Revenue Trend Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#0A2B1D]" />
            Revenue Trend (12 Months)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[280px]" data-testid="revenue-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3A6E53" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3A6E53" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="expenseGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#DC2626" stopOpacity={0.15}/>
                    <stop offset="95%" stopColor="#DC2626" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E8E6" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#8A948F' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#8A948F' }} axisLine={false} tickLine={false} tickFormatter={fmtShort} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="income" name="Income" stroke="#3A6E53" fill="url(#incomeGrad)" strokeWidth={2} />
                <Area type="monotone" dataKey="expenses" name="Expenses" stroke="#DC2626" fill="url(#expenseGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Pie Charts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Expense Breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ArrowDownRight className="w-4 h-4 text-red-500" />
              Expense Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {expenseByCategory.length > 0 ? (
              <div className="h-[240px]" data-testid="expense-pie">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={expenseByCategory}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {expenseByCategory.map((entry) => (
                        <Cell key={entry.name} fill={EXPENSE_COLORS[expenseByCategory.indexOf(entry) % EXPENSE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => fmt(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[240px] flex items-center justify-center text-[#8A948F] text-sm">No expenses to display</div>
            )}
          </CardContent>
        </Card>

        {/* Income Sources */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ArrowUpRight className="w-4 h-4 text-green-500" />
              Income Sources
            </CardTitle>
          </CardHeader>
          <CardContent>
            {incomeBySource.length > 0 ? (
              <div className="h-[240px]" data-testid="income-pie">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={incomeBySource}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {incomeBySource.map((entry) => (
                        <Cell key={entry.name} fill={COLORS[incomeBySource.indexOf(entry) % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => fmt(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[240px] flex items-center justify-center text-[#8A948F] text-sm">No income to display</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Monthly Comparison Bar Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Monthly Income vs Expenses</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[240px]" data-testid="monthly-bar-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E8E6" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#8A948F' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#8A948F' }} axisLine={false} tickLine={false} tickFormatter={fmtShort} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="income" name="Income" fill="#3A6E53" radius={[3, 3, 0, 0]} />
                <Bar dataKey="expenses" name="Expenses" fill="#EF4444" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Quick Summary Footer */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-[#E2ECE7] border-[#B4C9BE]">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-[#5C6661]">Total Income (All Time)</p>
            <p className="text-xl font-bold text-green-700">{fmt(totalIncome)}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#FEF2F2] border-red-200">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-[#5C6661]">Total Expenses (All Time)</p>
            <p className="text-xl font-bold text-red-600">{fmt(totalExpenses)}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#0A2B1D] border-[#0A2B1D]">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-[#B4C9BE]">Net Profit (All Time)</p>
            <p className="text-xl font-bold text-white">{fmt(totalIncome - totalExpenses)}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OverviewTab;
